import React from 'react';
import { Table, Space, Tooltip } from 'antd';
import { 
  TableWrapper, 
  StudentEditModal,
  StudentHistoryModal,
  B2cMailSendModal, 
  CompanyEditModal, 
  BillB2CRequestModal, 
  BillB2CPublicModal } from 'components/molecules';
import { usePopupManager } from 'react-popup-manager';
import { ColumnsType } from 'antd/lib/table/interface';
import { CarryOutOutlined, WarningOutlined } from '@ant-design/icons'

interface FormItemProps {
  info: LectureInfoDto;
  group: string;
  studentList: StudentListDto[];  
  refetch: () => void;
}

const PenaltyTable = ({info, group, studentList, refetch}: FormItemProps): React.ReactElement => {
  /* ======================================================
   * variables
   * ====================================================== */
  const { open } = usePopupManager();

  const onRowClick = (record) => {
    return {
      onClick: () => {
        open(StudentEditModal, { 
          id: record.id,
          group: group,
          lectureInfo: info,
          refetch: refetch,
          onClose: (isOk) => {
            if (isOk) {   
              refetch();
            };
          },          
        }); 
      },
    };
   };

  const columns: ColumnsType<StudentListDto> = [
    {
      title: '번호',
      key: 'num',
      width: '50px',
      align: 'center',
      fixed: 'left',
      onCell: onRowClick,  
      render: (text, record, index) => {
        return (
          index+1
        )
      }        
    },  
    {
      title: '결제수단',
      key: 'ebill_yn',
      dataIndex: 'ebill_yn',
      width: '100px',
      align: 'center',
      fixed: 'left',
      onCell: onRowClick,  
      render: (ebill_yn, record) => {
        return (
          record.mem_price==0?'장학생':(!!record.bill_type)?record.bill_type:(ebill_yn==='Y')?'세금계산서':(ebill_yn==='C')?'선납금':(ebill_yn==='N')?'카드':(ebill_yn==='P')?'포인트':'현금영수증'
        )
      }        
    }, 
    {
      title: '발행일자',
      key: 'bill_date',
      dataIndex: 'bill_date',
      width: '100px',
      align: 'center',
      fixed: 'left',
      onCell: onRowClick,  
      render: (bill_date, record) => {
        return (
          (record.bill_price===record.mem_price)?<a onClick={(e) => {
            e.stopPropagation();
          }}><label style={{color:'black'}}>{record.mem_price==0?record.ebill_etc:bill_date}</label></a>:<label style={{color:'red'}}>{record.ebill_etc}</label>
        )
      }        
    },      
    {
      title: '위약금 (vat 포함)',
      dataIndex: 'mem_price',
      key: 'mem_price',
      width: '150px',
      align: 'right',
      fixed: 'left',
      onCell: onRowClick,  
      render: (mem_price, record) => {
        const price = mem_price.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")+' ('+(mem_price*1.1).toFixed(0).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")+')';
        return (
          <a onClick={(e) => {
            e.stopPropagation();

            if (record.bill_price===record.mem_price) {
              open(BillB2CPublicModal, {
                id: record.bill_id,
                onClose: (isOk) => {
                  if (isOk) {}
                },
              }); 
            } else {
              open(BillB2CRequestModal, {
                id: record.group_id,
                student_id: record.id,
                onClose: (isOk) => {
                  if (isOk) {refetch()}
                },
              });  
            }
          }}>
            <label style={{color:(record.bill_price!==record.mem_price)?'red':'black'}}>{price}</label>
          </a>
        )
      }       
    },     
    {
      title: '포인트매출',
      dataIndex: 'bill_point',
      key: 'bill_point',
      width: '80px',
      align: 'right',
      fixed: 'left',
      onCell: onRowClick,  
      render: (bill_point) => {
        return (
          bill_point.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")
        )
      }    
    },       
    {
      title: '회사명',
      dataIndex: 'company_name',
      key: 'company_name',
      width: '130px',
      align: 'center', 
      ellipsis: true,  
      fixed: 'left', 
      sorter: (a, b) => a.company_name.localeCompare(b.company_name),
      onCell: onRowClick,  
      render: (company_name, record) => {
        return record.company_srl && record.edu_srl?
          <a onClick={(e) => {
            e.stopPropagation();
            open(CompanyEditModal, {
              id: record.company_srl,
              isPopup: true,
              onClose: (isOk) => {if(isOk) refetch();},
            }); 
          }}>
            <label style={{color:'black'}}>{company_name}</label>
          </a>:<label style={{color:'red'}}>{company_name}</label>        
      }          
    },  
    {
      title: '부서명',
      dataIndex: 'department',
      key: 'department',
      width: '120px',
      align: 'center',
      fixed: 'left',
      ellipsis: true,
      onCell: onRowClick,  
      sorter: (a, b) => a.department.localeCompare(b.department),
    },
    {
      title: '이름',
      dataIndex: 'name_kor',
      key: 'name_kor',
      width: '90px',
      align: 'center',
      fixed: 'left',
      ellipsis: true,  
      onCell: onRowClick,  
      sorter: (a, b) => a.name_kor.localeCompare(b.name_kor),
      render: (name_kor, record) => {
        return (
          <>
            {(record.memo)?(
              <Tooltip placement="topLeft" title={record.memo}>
                <CarryOutOutlined />&nbsp;{name_kor}
              </Tooltip>
              
            ):(name_kor)}
            {(record.memo3 !== '0')&&(<WarningOutlined style={{color:"red",marginLeft:"5px"}} />)}
          </>
        )
      }  
    }, 
    {
      title: '직위',
      dataIndex: 'position',
      key: 'position',
      width: '120px',
      align: 'center',
      fixed: 'left',
      ellipsis: true,
      onCell: onRowClick,  
    },
    {
      title: '직책',
      dataIndex: 'duty',
      key: 'duty',
      width: '120px',
      align: 'center',
      fixed: 'left',
      ellipsis: true,      
      onCell: onRowClick,  
    },          
    {
      title: '휴대폰',
      dataIndex: 'phone',
      key: 'phone',
      width: '100px',
      align: 'center',
      ellipsis: true,  
      onCell: onRowClick,  
      render: (phone) => {
        return (
          phone&&phone.toString().replace(/^(\d{2,3})(\d{3,4})(\d{4})$/, `$1-$2-$3`)
        )
      } 
    },    
    {
     title: '이메일',
     dataIndex: 'email',
     key: 'email',
     width: '150px',
     align: 'center',
     ellipsis: true,   
     onCell: onRowClick,  
   },
 {
  title: '담당자명',
  dataIndex: 'edu_name',
  key: 'edu_name',
  width: '80px',
  align: 'center',
  ellipsis: true,    
  onCell: onRowClick,  
  sorter: (a, b) => a.edu_name.localeCompare(b.edu_name),   
},    
{
  title: '담당자직급',
  dataIndex: 'edu_position',
  key: 'edu_position',
  width: '80px',
  align: 'center',
  ellipsis: true,  
  onCell: onRowClick,  
  sorter: (a, b) => a.edu_position.localeCompare(b.edu_position),      
},    
{
  title: '담당자전화',
  dataIndex: 'edu_phone',
  key: 'edu_phone',
  width: '100px',
  align: 'center',
  ellipsis: true,  
  onCell: onRowClick,     
  render: (phone) => {
    return (
      phone&&phone.toString().replace(/^(\d{2,3})(\d{3,4})(\d{4})$/, `$1-$2-$3`)
    )
  } 
},    
{
  title: '담당자이메일',
  dataIndex: 'edu_email',
  key: 'edu_email',
  width: '150px',
  align: 'center',
  ellipsis: true,  
  onCell: onRowClick,     
},    
  ];

  /* ======================================================
  * hooks
  * ====================================================== */

  /* ======================================================
  * events
  * ====================================================== */  

  const onSendMail = (id: number, mail_type: string) => {          
    if (!!info) {
        open(B2cMailSendModal, {
            id: id,
            group: group,
            mail_type: mail_type,
            onClose: (isOk) => {
              if (isOk) {
                refetch();
              };
            },
        });  
    }
  }   

  const onHistoryOpen = (name: string, phone: string) => {          
    open(StudentHistoryModal, {
      name_kor: name,
      phone: phone,
    }); 
  }     

  /* ======================================================
   * render items
   * ====================================================== */
  return (
    <Space direction="vertical" style={{ width: '100%' }}>
        <TableWrapper isSearch={false}>
          <Table
            tableLayout="fixed"
            scroll={{ x: 1450 }}
            dataSource={studentList}
            columns={columns}
            pagination={false}
            style={{ width: '100%', overflowX: 'auto', minWidth: 600 }}
            className="columnCenterFixTable clickable"
            showSorterTooltip={false}
            rowKey={'id'}
          />
        </TableWrapper> 
    </Space>
  );
};

export default PenaltyTable;
